// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { Job } from '../api/types';
import { isTerminal } from '../api/types';
import type { StageCard } from './stageModel';

/**
 * The typed solid model — the building model the 3D view (Bygning) draws —
 * in run order: storeys and plane classes feed the walls, the walls carry
 * the openings, spaces and solids complete it. "Opdatér bygningsmodel"
 * submits exactly this chain (`POST /job-chains`, ruxd-0sk); a stage whose
 * predecessor fails is cancelled by the server rather than run on stale
 * input.
 */
export const GEOMETRY_CHAIN = [
  'storeys',
  'classify',
  'walls',
  'openings',
  'spaces',
  'solids',
] as const;

/** Danish card titles. The wire token stays visible next to it (`command`). */
const STAGE_TITLES: Record<string, string> = {
  optimize: 'Optimér kamerapositioner',
  clouds: 'Punktsky',
  planes: 'Planer',
  rooms: 'Rumsegmentering',
  instances: 'Instanser',
  annotate: 'Semantisk annotering',
  storeys: 'Etager',
  classify: 'Planklassificering',
  walls: 'Vægge',
  openings: 'Åbninger',
  spaces: 'Rum',
  solids: 'Solider',
  mesh: 'Cellekompleks-mesh (ældre)',
};

export function stageTitle(stage: string): string {
  return STAGE_TITLES[stage] ?? stage;
}

export interface StageGroup {
  id: string;
  title: string;
  /** One sentence on what the group is for; null for none. */
  description: string | null;
  stages: readonly string[];
}

export const STAGE_GROUPS: readonly StageGroup[] = [
  {
    id: 'scan',
    title: 'Scanning og punktsky',
    description: null,
    stages: ['optimize', 'clouds'],
  },
  {
    id: 'segmentation',
    title: 'Segmentering',
    description: null,
    stages: ['planes', 'rooms', 'instances', 'annotate'],
  },
  {
    id: 'building',
    title: 'Bygningsmodel',
    description:
      'Den typede model af vægge, åbninger, rum og etageadskillelser, som 3D-visningen viser.',
    stages: GEOMETRY_CHAIN,
  },
  {
    id: 'legacy',
    title: 'Ældre cellekompleks-mesh',
    description:
      'Ét samlet, vandtæt mesh fra cellekomplekset (MIP-løsning). Opdaterer ikke bygningsmodellen. ' +
      'Kører på GPU-workeren med cuOpt, når en er online, ellers på serveren.',
    stages: ['mesh'],
  },
];

const OTHER_GROUP: StageGroup = {
  id: 'other',
  title: 'Øvrige trin',
  description: null,
  stages: [],
};

/**
 * The cards sorted into STAGE_GROUPS (in group order, then the group's own
 * stage order). A stage the server lists but no group names lands in
 * "Øvrige trin" — never dropped. Empty groups are left out.
 */
export function groupStageCards<T extends { stage: string }>(
  cards: T[],
): { group: StageGroup; cards: T[] }[] {
  const out: { group: StageGroup; cards: T[] }[] = [];
  const placed = new Set<string>();
  for (const group of STAGE_GROUPS) {
    const members = group.stages
      .map((stage) => cards.find((card) => card.stage === stage))
      .filter((card): card is T => card !== undefined);
    members.forEach((card) => placed.add(card.stage));
    if (members.length > 0) out.push({ group, cards: members });
  }
  const rest = cards.filter((card) => !placed.has(card.stage));
  if (rest.length > 0) out.push({ group: OTHER_GROUP, cards: rest });
  return out;
}

export type ChainState = 'idle' | 'running' | 'succeeded' | 'failed';

export interface ChainView {
  state: ChainState;
  /** Finished stages of the submitted chain. */
  done: number;
  total: number;
  /** Title of the stage running or next up; null when not running. */
  current: string | null;
  /** One sentence for the panel: progress, outcome, or why it cannot run. */
  message: string | null;
  canRun: boolean;
}

/**
 * What the "Opdatér bygningsmodel" panel shows.
 *
 * @param chain the job ids the last chain submission returned, in order, or
 *   null when none was submitted this session.
 * @param cards the stage cards (readiness of the chain's first stage).
 */
export function geometryChainView(
  chain: string[] | null,
  jobs: Job[],
  cards: StageCard[],
): ChainView {
  const total = GEOMETRY_CHAIN.length;
  const first = cards.find((card) => card.stage === GEOMETRY_CHAIN[0]);
  const chainBusy = GEOMETRY_CHAIN.some((stage) => {
    const card = cards.find((c) => c.stage === stage);
    return card?.state === 'queued' || card?.state === 'running';
  });

  if (chain && chain.length > 0) {
    const records = chain.map((id) => jobs.find((job) => job.id === id) ?? null);
    const done = records.filter((job) => job?.status === 'succeeded').length;
    const failedAt = records.findIndex(
      (job) => job !== null && isTerminal(job.status) && job.status !== 'succeeded',
    );
    const allTerminal = records.every((job) => job !== null && isTerminal(job.status));
    if (failedAt >= 0 && (allTerminal || !chainBusy)) {
      const job = records[failedAt]!;
      const title = stageTitle(job.stage);
      const why = job.error ? `: ${job.error.replace(/\.$/, '')}.` : '.';
      return {
        state: 'failed',
        done,
        total: chain.length,
        current: null,
        message:
          job.status === 'cancelled'
            ? `Stoppet ved ${title}${why}`
            : `${title} fejlede${why} De efterfølgende trin blev ikke kørt.`,
        canRun: !chainBusy && canStart(first),
      };
    }
    if (allTerminal) {
      return {
        state: 'succeeded',
        done,
        total: chain.length,
        current: null,
        message: 'Bygningsmodellen er opdateret.',
        canRun: canStart(first),
      };
    }
    const next = records.find((job) => job === null || !isTerminal(job.status));
    const current = next ? stageTitle(next.stage) : null;
    return {
      state: 'running',
      done,
      total: chain.length,
      current,
      message: current ? `Trin ${done + 1} af ${chain.length}: ${current}` : null,
      canRun: false,
    };
  }

  if (!first) {
    return {
      state: 'idle',
      done: 0,
      total,
      current: null,
      message: 'Serveren kender ikke bygningsmodellens trin (for gammel ruxd).',
      canRun: false,
    };
  }
  return {
    state: chainBusy ? 'running' : 'idle',
    done: 0,
    total,
    current: null,
    message: chainBusy
      ? 'Et af bygningsmodellens trin kører allerede.'
      : first.blocked
        ? `Kan ikke køre endnu — ${first.blocked}`
        : null,
    canRun: !chainBusy && canStart(first),
  };
}

function canStart(first: StageCard | undefined): boolean {
  return first !== undefined && first.runnable && first.ready;
}
